import { setSessionStorage, getSessionStorage } from 'utils/sessionStorage';
import { compareUrlPathWithStep } from 'utils/string';
import { COUNTRY_CODE_AU, COUNTRY_CODE_NZ } from './countryCodes';
import {
  PRODUCTS,
  STEP_LANDING,
  STEP_NZ_GEM_V2,
  STEP_CONTACT_DETAILS,
  STEP_RESUME_YOUR_APPLICATION,
  STEP_VERIFY_TO_RESUME,
  STEP_VERIFY,
  STEP_VERIFY_APPLICATION_MOBILE,
  STEP_PASSWORD,
  STEP_ABOUT_YOU,
  STEP_BIOMETRICS_ABOUT_YOU,
  STEP_BIOMETRICS_ADDRESS_DETAILS,
  STEP_ADDRESS_DETAILS,
  STEP_IDENTIFICATION,
  STEP_INCOME,
  STEP_EXPENSES,
  STEP_COMMITMENTS,
  STEP_CREDIT_LIMIT,
  STEP_ACCEPTANCE,
  STEP_PRODUCT_COMPARISON,
  STEP_DAVID_JONES_PRESTIGE,
  STEP_DAVID_JONES_PREMIERE,
  STEP_TWENTY_EIGHT_DEGREES,
  STEP_GO,
  STEP_GEM_AU,
  STEP_CREDIT_LINE,
  STEP_LOW_RATE,
  STEP_VERIFY_DOCUMENT_UPLOAD,
  STEP_OUTCOME_FIN_TABLE,
  STEP_AGREE_AND_CONTINUE,
  PRODUCT_CODE_NZ_GEM,
  PRODUCT_CODE_TWENTY_EIGHT_DEGREES,
  PRODUCT_CODE_DAVID_JONES_PREMIERE,
  PRODUCT_CODE_DAVID_JONES_PRESTIGE,
  PRODUCT_CODE_GO,
  PRODUCT_CODE_AU_GEM,
  PRODUCT_CODE_LOW_RATE,
  PRODUCT_CODE_AFS,
} from './_constants/products';

export const sessionStorageKey = 'config';

export * from './_constants';

const configSingleton = {};

export const setConfig = ({ productCode, countryCode: countryCodeInput }) => {
  const countryCode = countryCodeInput || lookupCountryCode(productCode);
  const nzV2Config = countryCode === COUNTRY_CODE_NZ;

  let importCountryConfig = null;
  switch (countryCode) {
    case COUNTRY_CODE_NZ:
      importCountryConfig = () => require('./nz');
      break;
    case COUNTRY_CODE_AU:
      importCountryConfig = () => require('./au');
      break;
    default:
      throw new Error(`Unexpected country code: ${countryCode}`);
  }

  const countryConfig = importCountryConfig();
  Object.assign(configSingleton, {
    countryCode,
    productCode,
    isNzV2: nzV2Config,
    productName: countryCode && productCode ? PRODUCTS[countryCode][productCode].name : null,
    phoneNumbers: productCode && countryConfig?.getPhoneNumbers(productCode),
    ...countryConfig,
  });

  setSessionStorage(sessionStorageKey, {
    countryCode,
    productCode,
    isNzV2: nzV2Config,
  });

  return configSingleton;
};

const configMap = {
  [STEP_DAVID_JONES_PRESTIGE]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_DAVID_JONES_PRESTIGE,
    });
  },
  [STEP_DAVID_JONES_PREMIERE]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_DAVID_JONES_PREMIERE,
    });
  },
  [STEP_PRODUCT_COMPARISON]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
    });
  },
  [STEP_TWENTY_EIGHT_DEGREES]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_TWENTY_EIGHT_DEGREES,
    });
  },
  [STEP_GO]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_GO,
    });
  },
  [STEP_GEM_AU]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_AU_GEM,
    });
  },
  [STEP_CREDIT_LINE]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_AFS,
    });
  },
  [STEP_LOW_RATE]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
      productCode: PRODUCT_CODE_LOW_RATE,
    });
  },
  [STEP_OUTCOME_FIN_TABLE]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_AU,
    });
  },
  [STEP_RESUME_YOUR_APPLICATION]: () => {
    const queryString = window.location.search;
    const queries = new URLSearchParams(queryString);
    const cardType = queries.get('cardType');
    const productCode = queries.get('productCode');
    if (!productCode && cardType) {
      return setConfig({ countryCode: cardType === 'DJ' ? COUNTRY_CODE_AU : COUNTRY_CODE_NZ });
    }
    if (productCode) {
      const countryCode = lookupCountryCode(productCode);
      return setConfig({
        countryCode,
        productCode,
      });
    }
    return setConfig({
      countryCode: COUNTRY_CODE_NZ,
      productCode: PRODUCT_CODE_NZ_GEM,
    });
  },
  [STEP_LANDING]: () => {
    return setConfig({
      countryCode: COUNTRY_CODE_NZ,
      productCode: PRODUCT_CODE_NZ_GEM,
    });
  },
};

const initConfig = () => {
  const stepName = Object.keys(configMap).find(step => compareUrlPathWithStep(step, window.location.pathname));
  if (stepName) {
    return configMap[stepName]();
  }

  if (getSessionStorage(sessionStorageKey)) {
    // This is to prevent when user hits refresh and then config always get reset to default NZ settings
    const prevConfig = getSessionStorage(sessionStorageKey);
    return setConfig(prevConfig);
  }

  return setConfig({
    countryCode: COUNTRY_CODE_NZ,
    productCode: PRODUCT_CODE_NZ_GEM,
  });
};

export const config = Object.keys(configSingleton).length ? configSingleton : initConfig();

export function lookupCountryCode(productCode) {
  const matchedPair = Object.entries(PRODUCTS).find(([country, products]) =>
    Object.keys(products).includes(productCode),
  );

  if (!matchedPair) {
    throw new Error(`Unexpected product code: ${productCode}`);
  }

  return matchedPair[0];
}

export const stepToFormMap = {
  [STEP_LANDING]: 'overview',
  [STEP_NZ_GEM_V2]: 'overview',
  [STEP_CONTACT_DETAILS]: 'contactDetails',
  [STEP_RESUME_YOUR_APPLICATION]: 'contactDetails',
  [STEP_VERIFY_TO_RESUME]: 'verification',
  [STEP_VERIFY]: 'verification',
  [STEP_VERIFY_APPLICATION_MOBILE]: 'verification',
  [STEP_PASSWORD]: 'password',
  [STEP_ABOUT_YOU]: 'personalDetails',
  [STEP_BIOMETRICS_ABOUT_YOU]: 'biometricsAboutYou',
  [STEP_BIOMETRICS_ADDRESS_DETAILS]: 'biometricsAddressDetails',
  [STEP_ADDRESS_DETAILS]: 'otherDetails',
  [STEP_IDENTIFICATION]: 'extraDetails',
  [STEP_INCOME]: 'employmentDetails',
  [STEP_EXPENSES]: 'expensesDetails',
  [STEP_COMMITMENTS]: 'additionalDebtsDetails',
  [STEP_CREDIT_LIMIT]: 'creditLimitDetails',
  [STEP_ACCEPTANCE]: 'acceptance',
  [STEP_PRODUCT_COMPARISON]: 'comparison',
  [STEP_LANDING]: 'overview',
  [STEP_DAVID_JONES_PRESTIGE]: 'overview',
  [STEP_DAVID_JONES_PREMIERE]: 'overview',
  [STEP_TWENTY_EIGHT_DEGREES]: 'overview',
  [STEP_GO]: 'overview',
  [STEP_GEM_AU]: 'overview',
  [STEP_CREDIT_LINE]: 'overview',
  [STEP_LOW_RATE]: 'overview',
  [STEP_VERIFY_DOCUMENT_UPLOAD]: 'employmentDetails',
  [STEP_AGREE_AND_CONTINUE]: 'agreeAndContinue',
};

export const formToStepMap = Object.keys(stepToFormMap).reduce(
  (acc, key) => ({
    ...acc,
    [stepToFormMap[key]]: key,
  }),
  {},
);
